import _ from 'lodash';
import { getIntl } from 'umi';
import React from 'react';

import Badge from '@/components/Badge';
import { isJsonLike } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import status from '@/utils/infrastructure/utilities/status';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { getEnumNamespace } from '@/utils/infrastructure';

export const reqOriginButtons = [
  {
    value: CoreEnums.ProcessInstanceRequestOrigin.MyRequests,
    label: 'applications.text.myRequests',
  },
  {
    value: CoreEnums.ProcessInstanceRequestOrigin.ReceivedRequests,
    label: 'applications.text.receivedRequests',
  },
  {
    value: CoreEnums.ProcessInstanceRequestOrigin.RevisionRequests,
    label: 'applications.text.revisionRequests',
  },
  { value: CoreEnums.ProcessInstanceRequestOrigin.All, label: 'applications.text.allRequests' },
  {
    value: CoreEnums.ProcessInstanceRequestOrigin.BulkRequests,
    label: 'applications.text.bulkRequests',
  },
];

export const filtersConfig = [
  {
    type: 'select',
    name: 'startedMode',
    label: 'processes.form.startMode',
    allowClear: true,
    options: [
      { label: 'process.text.manually', value: 'Instant' },
      { label: 'process.text.automatic', value: 'Automated' },
      { label: 'process.text.schedule', value: 'Scheduled' },
    ],
  },
  {
    type: 'select',
    name: 'processInstanceId',
    label: 'global.text.group',
    apiRoute: 'processGroup/paged',
    params: { select: 'id,name', filter: 'isActive eq true' },
    nomenclature: true,
    showSearch: true,
    allowClear: true,
    remote: true,
    options: [],
    lazy: true,
  },
  {
    type: 'select',
    name: 'processId',
    label: 'global.text.process',
    apiRoute: 'process/paged',
    params: {
      select: 'id,name',
      filter: 'isActive eq true',
      searchSelect: ['name'],
    },
    knownType: CoreEnums.FormFieldType.InternalEntity,
    nomenclature: true,
    showSearch: true,
    allowClear: true,
    remote: true,
    options: [],
    lazy: true,
  },
];

const StatusFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.ActionInstanceStatus),
  enumSource: CoreEnums.ActionInstanceStatus,
  options: [
    {
      label: 'applications.text.action.instanceStatus0',
      value: CoreEnums.ActionInstanceStatus.Pending,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus1',
      value: CoreEnums.ActionInstanceStatus.Finished,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus2',
      value: CoreEnums.ActionInstanceStatus.Rejected,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus3',
      value: CoreEnums.ActionInstanceStatus.Overdue,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus4',
      value: CoreEnums.ActionInstanceStatus.Skipped,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus5',
      value: CoreEnums.ActionInstanceStatus.Error,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus6',
      value: CoreEnums.ActionInstanceStatus.Execution,
      locale: true,
    },
  ],
};

export const iterationResultsFiltersConfig = [StatusFilter];

// Dynamic Entity Operation Status Filter
const DynamicEntityStatusFilter = {
  ...Filters.BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.ActionInstanceStatus),
  enumSource: CoreEnums.ActionInstanceStatus,
  options: [
    {
      label: 'applications.text.action.instanceStatus0',
      value: CoreEnums.ActionInstanceStatus.Pending,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus1',
      value: CoreEnums.ActionInstanceStatus.Finished,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus2',
      value: CoreEnums.ActionInstanceStatus.Rejected,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus3',
      value: CoreEnums.ActionInstanceStatus.Overdue,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus4',
      value: CoreEnums.ActionInstanceStatus.Skipped,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus5',
      value: CoreEnums.ActionInstanceStatus.Error,
      locale: true,
    },
    {
      label: 'applications.text.action.instanceStatus6',
      value: CoreEnums.ActionInstanceStatus.Execution,
      locale: true,
    },
  ],
};

export const dynamicEntityQueryResultsFiltersConfig = [DynamicEntityStatusFilter];

registerModuleFilters('iterationResults', [StatusFilter]);
registerModuleFilters('dynamicEntityQueryResults', [DynamicEntityStatusFilter]);

export const commandResponseMessage = {
  [CoreEnums.RequestDetailCommandType.Approve]: 'applications.text.message.commandType1',
  [CoreEnums.RequestDetailCommandType.Reject]: 'applications.text.message.commandType2',
  [CoreEnums.RequestDetailCommandType.Complete]: 'applications.text.message.commandType3',
  [CoreEnums.RequestDetailCommandType.ExecuteTask]: 'applications.text.message.commandType4',
  [CoreEnums.RequestDetailCommandType.Sign]: 'applications.text.message.commandType5',
  [CoreEnums.RequestDetailCommandType.Cancel]: 'applications.text.message.commandType6',
  [CoreEnums.RequestDetailCommandType.Void]: 'applications.text.message.commandType7',
  [CoreEnums.RequestDetailCommandType.SendNotifications]: 'applications.text.message.commandType8',
  [CoreEnums.RequestDetailCommandType.EvaluateApprovers]: 'applications.text.message.commandType9',
  [CoreEnums.RequestDetailCommandType.BackToStep]: 'applications.text.message.commandType10',
  [CoreEnums.RequestDetailCommandType.ReassignTask]: 'applications.text.message.commandType11',
  [CoreEnums.RequestDetailCommandType.ExecuteAutomaticAction]:
    'applications.text.message.commandType12',
  [CoreEnums.RequestDetailCommandType.AutoAssignTask]: 'applications.text.message.commandType15',
  [CoreEnums.RequestDetailCommandType.AutoCancel]: 'applications.text.message.commandType28',
  [CoreEnums.RequestDetailCommandType.AutoVoid]: 'applications.text.message.commandType29',
};

export const commandMessageType = {
  [CoreEnums.RequestDetailCommandType.Approve]: 'success',
  [CoreEnums.RequestDetailCommandType.ExecuteTask]: 'success',
  [CoreEnums.RequestDetailCommandType.Complete]: 'success',
  [CoreEnums.RequestDetailCommandType.Reject]: 'error',
  [CoreEnums.RequestDetailCommandType.Cancel]: 'error',
};

export function mapConditionEvaluationTableColumns() {
  const { formatMessage } = getIntl();
  return [
    {
      title: formatMessage({ id: 'applications.text.valueSource' }),
      dataIndex: 'valueSource',
      width: 120,
      render: (val) => formatMessage({ id: 'applications.text.valueSourceType' + val }),
    },
    { title: formatMessage({ id: 'applications.text.form' }), dataIndex: 'form', width: 200 },
    { title: formatMessage({ id: 'applications.text.field' }), dataIndex: 'field', width: 200 },
    {
      title: formatMessage({ id: 'applications.text.variable' }),
      dataIndex: 'variable',
      width: 100,
    },
    {
      title: formatMessage({ id: 'applications.text.operator' }),
      dataIndex: 'operator',
      width: 100,
      render: (val) => {
        if (_.isNil(val)) return;
        return formatMessage({ id: `applications.text.comparisonOperatorType${val}` });
      },
    },
    {
      title: formatMessage({ id: 'applications.text.conditionValue' }),
      dataIndex: 'conditionValue',
      width: 130,
    },
    {
      title: formatMessage({ id: 'applications.text.evaluationValue' }),
      dataIndex: 'evaluationValue',
      width: 130,
    },
    {
      title: formatMessage({ id: 'applications.text.evaluationObservations' }),
      width: 280,
      render: (r) => (
        <Badge
          type="process"
          status={r.isSuccessfullyResult ? 'success' : 'error'}
          text={r.evaluationObservations}
        />
      ),
    },
  ];
}

export function mapFormDataTableColumns() {
  const { formatMessage } = getIntl();
  return [
    {
      title: formatMessage({ id: 'applications.text.originValue' }),
      dataIndex: 'originValue',
    },
    {
      title: formatMessage({ id: 'applications.text.destinationForm' }),
      dataIndex: 'destinationForm',
    },
    {
      title: formatMessage({ id: 'applications.text.destinationField' }),
      dataIndex: 'destinationField',
    },
    {
      title: formatMessage({ id: 'applications.text.value' }),
      dataIndex: 'value',
    },
  ];
}

export function mapDynamicEntityQueryTableColumns() {
  const { formatMessage } = getIntl();

  const statusList = _.chain(CoreEnums.ProcessInstanceStatus)
    .values()
    .map((num) => {
      return {
        text: formatMessage({ id: `applications.text.action.instanceStatus${num}` }),
        value: num,
      };
    })
    .value();

  return [
    { title: '#', dataIndex: 'executionOrder' },
    {
      sorter: true,
      dataIndex: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      onFilter: (val, rec) => rec.status === val,
      filters: statusList,
      render: (val) => {
        return (
          <Badge
            type="process"
            status={status.processStatus[val || 0]?.name}
            text={formatMessage({ id: `applications.text.action.instanceStatus${val || 0}` })}
          />
        );
      },
    },
    {
      sorter: true,
      dataIndex: 'instanceData',
      title: formatMessage({ id: 'applications.text.insertedData' }),
      render: (r) => {
        const data = isJsonLike(r) ? JSON.parse(r) : r;
        return _.head(data)?.itemId || '-';
      },
    },
  ];
}

// OperationResultList configuration
export const operationResultListConfig = {
  [CoreEnums.ActionType.Iterate]: {
    hasFilters: true,
    hasOrderBy: true,
    hasSearch: true,
    enableStatusUpdates: true,
    defaultOrderBy: 'executionOrder asc',
    orderByField: 'executionOrder',
    modelName: 'iterationResults',
    searchKey: 'iteration-results-search',
    type: 'iteration-item',
  },
  [CoreEnums.ActionType.Switch]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'executionOrder asc',
    orderByField: 'executionOrder',
    modelName: 'iterationResults',
    searchKey: 'switch-results-search',
    type: 'switchCase-item',
  },
  [CoreEnums.ActionType.ConsumeSequence]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'consumeSequenceOperationResult',
    searchKey: 'consume-sequence-results-search',
    type: 'consume-sequence-item',
  },
  [CoreEnums.ActionType.SendEmail]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'iterationResults',
    searchKey: 'sendemail-results-search',
    type: 'sendEMail-item',
  },
  [CoreEnums.ActionType.ExecuteApiCall]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'apiCallResults',
    searchKey: 'apicall-results-search',
    type: 'apiCall-item',
  },
  [CoreEnums.ActionType.Integration]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'apiCallResults',
    searchKey: 'integration-results-search',
    type: 'apiCall-item',
  },
  [CoreEnums.ActionType.RunCode]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'runCodeResults',
    searchKey: 'runcode-results-search',
    type: 'runCode-item',
  },
  [CoreEnums.ActionType.ExecuteRpaFlow]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'rpaCallResults',
    searchKey: 'rpacall-results-search',
    type: 'rpaCall-item',
  },
  [CoreEnums.ActionType.InternalOperation]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'internalOperationResults',
    searchKey: 'internaloperation-results-search',
    type: 'internalOperation-item',
  },
  [CoreEnums.ActionType.OpenAI]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'openAICallResults',
    searchKey: 'openaicall-results-search',
    type: 'openai-item',
  },
  [CoreEnums.ActionType.AutomyAI]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'automyAIResult',
    searchKey: 'automyai-results-search',
    type: 'automyai-item',
  },
  [CoreEnums.ActionType.DynamicEntityOperation]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'dynamicEntityResults',
    searchKey: 'dynamicentity-results-search',
    type: 'dynamicEntity-item',
  },
  [CoreEnums.ActionType.CompleteInformation]: {
    hasOrderBy: true,
    hasSearch: true,
    defaultOrderBy: 'created asc',
    orderByField: 'created',
    modelName: 'webhookOperationResults',
    searchKey: 'webhook-results-search',
    type: 'webhook-item',
  },
};

export const getOperationResultListConfig = (actionType, isQueryOperation = false) => {
  const config =
    operationResultListConfig[actionType] ||
    operationResultListConfig[CoreEnums.ActionType.Iterate];

  if (actionType === CoreEnums.ActionType.DynamicEntityOperation && isQueryOperation) {
    return {
      ...config,
      hasFilters: true,
      type: 'dynamicEntityQuery-item',
      defaultOrderBy: 'executionOrder asc',
      orderByField: 'executionOrder',
      modelName: 'dynamicEntityQueryResults',
    };
  }

  return config;
};
